import ApplicationController from "./application_controller";

// Attached to each <tr> in the domains TLS table.
// Manages all per-domain TLS actions (verify DNS, initiate cert, update status, renew cert)
// and handles in-place cell updates so the DataTable filter/sort state is preserved.
export default class TlsDomainActionsController extends ApplicationController {
  static targets = [
    'tlsStatus',
    'validationStatus',
    'expiry',
    'statusError',
    'renewalError',
    'cnameStatus'
  ]

  // ─── Actions ───────────────────────────────────────────────────────────────

  verifyDns(event) {
    const btn      = event.currentTarget;
    const origHtml = btn.innerHTML;

    this.setLoading(btn, '<i class="fa fa-spinner fa-spin"></i> Verifying...');
    if (this.hasCnameStatusTarget) {
      this.cnameStatusTarget.className = 'badge bg-warning';
      this.cnameStatusTarget.textContent = 'Checking...';
    }

    $.ajax({
      url:     '/mailgun/domains/tls/verify_dns',
      method:  'POST',
      dataType: 'json',
      data:    { domain_id: this.domainId },
      headers: { 'X-CSRF-Token': this.csrfToken() },
      success: (response) => {
        if (response.success) {
          if (this.hasCnameStatusTarget) {
            this.cnameStatusTarget.className = 'badge bg-success';
            this.cnameStatusTarget.textContent = 'Verified';
          }
          alertCustom(this.buildDnsResponseHtml(response), { alertType: 'success', displayPlacement: 'hover', icon: 'check' });
        } else {
          if (this.hasCnameStatusTarget) {
            this.cnameStatusTarget.className = 'badge bg-danger';
            this.cnameStatusTarget.textContent = 'Not Verified';
          }
          alertCustom(this.buildDnsResponseHtml(response), { alertType: 'error', displayPlacement: 'hover', icon: 'error' });
        }
      },
      error: (xhr) => {
        if (this.hasCnameStatusTarget) {
          this.cnameStatusTarget.className = 'badge bg-danger';
          this.cnameStatusTarget.textContent = 'Error';
        }
        const resp     = xhr.responseJSON || {};
        const errorMsg = resp.message || 'Error verifying DNS records';
        alertCustom(this.buildDnsResponseHtml({ message: errorMsg, data: resp.data }), { alertType: 'error', displayPlacement: 'hover', icon: 'error' });
      },
      complete: () => this.restoreButton(btn, origHtml)
    });
  }

  initiateCertificate(event) {
    if (!confirm(`Are you sure you want to initiate certificate creation for ${this.domainName}? This will verify DNS records first.`)) {
      return;
    }

    const btn      = event.currentTarget;
    const origHtml = btn.innerHTML;
    this.setLoading(btn, '<i class="fa fa-spinner fa-spin"></i> Initiating...');

    $.ajax({
      url:     '/mailgun/domains/tls/initiate_certificate',
      method:  'POST',
      dataType: 'json',
      data:    { domain_id: this.domainId },
      headers: { 'X-CSRF-Token': this.csrfToken() },
      success: (response) => {
        if (response.success) {
          alertCustom(
            'Certificate initiation requested. A background job will check the updated status in ~5 minutes.',
            { alertType: 'success', displayPlacement: 'hover', icon: 'check' }
          );
        } else {
          alertCustom(`Certificate initiation failed: ${response.message}`, { alertType: 'error', displayPlacement: 'hover', icon: 'error' });
        }
      },
      error: (xhr) => {
        const errorMsg = xhr.responseJSON?.message || 'Error initiating certificate';
        alertCustom(`Certificate initiation failed: ${errorMsg}`, { alertType: 'error', displayPlacement: 'hover', icon: 'error' });
      },
      complete: () => this.restoreButton(btn, origHtml)
    });
  }

  updateTlsStatus(event) {
    const btn      = event.currentTarget;
    const origHtml = btn.innerHTML;
    this.setLoading(btn, '<i class="fa fa-spinner fa-spin"></i> Updating...');

    $.ajax({
      url:     '/mailgun/domains/tls/update_tls_status',
      method:  'POST',
      dataType: 'json',
      data:    { domain_id: this.domainId },
      headers: { 'X-CSRF-Token': this.csrfToken() },
      success: (response) => {
        this.updateCells(response);
        alertCustom(`TLS status updated for ${this.domainName}`, { alertType: 'success', displayPlacement: 'hover', icon: 'check' });
      },
      error: (xhr) => {
        const response = xhr.responseJSON || {};
        if (response.validation_status) { this.updateCells(response); }
        const errorMsg = response.message || 'Error updating TLS status';
        alertCustom(`TLS status update failed: ${errorMsg}`, { alertType: 'error', displayPlacement: 'hover', icon: 'error' });
      },
      complete: () => this.restoreButton(btn, origHtml)
    });
  }

  renewCertificate(event) {
    if (!confirm(`Are you sure you want to renew the certificate for ${this.domainName}?`)) {
      return;
    }

    const btn      = event.currentTarget;
    const origHtml = btn.innerHTML;
    this.setLoading(btn, '<i class="fa fa-spinner fa-spin"></i> Renewing...');

    $.ajax({
      url:     '/mailgun/domains/tls/renew_certificate',
      method:  'POST',
      dataType: 'json',
      data:    { domain_id: this.domainId },
      headers: { 'X-CSRF-Token': this.csrfToken() },
      success: (response) => {
        alertCustom(`Certificate renewal requested successfully: ${response.message}`, { alertType: 'success', displayPlacement: 'hover', icon: 'check' });
        setTimeout(() => location.reload(), 2000);
      },
      error: (xhr) => {
        const errorMsg = xhr.responseJSON?.message || 'Error renewing certificate';
        alertCustom(`Certificate renewal failed: ${errorMsg}`, { alertType: 'error', displayPlacement: 'hover', icon: 'error' });
      },
      complete: () => this.restoreButton(btn, origHtml)
    });
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  get domainId()   { return this.element.dataset.domainId; }
  get domainName() { return this.element.dataset.domainName; }
  get removeOnResolved() { return this.element.dataset.removeOnResolved === 'true'; }

  updateCells(data) {
    const status       = data.validation_status || 'unknown';
    const statusError  = data.status_error_message;
    const renewalError = data.renewal_error_message;
    const expiryIso    = data.certificate_expiry_date;

    if (this.hasValidationStatusTarget) {
      this.validationStatusTarget.innerHTML  = this.validationStatusBadge(status);
      this.validationStatusTarget.dataset.order = status;
    }
    if (this.hasTlsStatusTarget) {
      this.tlsStatusTarget.innerHTML  = this.tlsStatusIcon(status);
      this.tlsStatusTarget.dataset.order = status;
    }
    if (this.hasExpiryTarget) {
      this.expiryTarget.innerHTML  = this.formatExpiryDate(expiryIso);
      this.expiryTarget.dataset.order = expiryIso ? Math.floor(new Date(expiryIso).getTime() / 1000) : 0;
    }
    if (this.hasStatusErrorTarget)  { this.statusErrorTarget.innerHTML  = this.errorCellHtml(statusError); }
    if (this.hasRenewalErrorTarget) { this.renewalErrorTarget.innerHTML = this.errorCellHtml(renewalError); }

    this.removeIfResolved(status);
  }

  removeIfResolved(status) {
    if (!this.removeOnResolved) { return; }
    if (status !== 'expired' && status !== 'error') {
      this.element.style.transition = 'opacity 0.4s';
      this.element.style.opacity    = '0';
      setTimeout(() => this.element.remove(), 400);
    }
  }

  setLoading(btn, html) {
    btn.disabled  = true;
    btn.innerHTML = html;
  }

  restoreButton(btn, html) {
    btn.disabled  = false;
    btn.innerHTML = html;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  validationStatusBadge(status) {
    const classes = {
      active:     'bg-success',
      expired:    'bg-danger',
      error:      'bg-danger',
      processing: 'bg-warning'
    };
    const cls   = classes[status] || 'bg-secondary';
    const label = status ? (status.charAt(0).toUpperCase() + status.slice(1)) : 'Unknown';
    return `<span class="badge ${cls}">${label}</span>`;
  }

  tlsStatusIcon(status) {
    const icons = {
      active:     `<i class="fa fa-lock align-middle me-2 text-success" title="TLS Secure" aria-hidden="true"></i>`,
      expired:    `<i class="fa fa-lock-open align-middle me-2 text-danger" title="TLS Certificate Expired" aria-hidden="true"></i>`,
      error:      `<i class="fa fa-exclamation-triangle align-middle me-2 text-danger" title="TLS Error" aria-hidden="true"></i>`,
      processing: `<i class="fa fa-clock align-middle me-2 text-warning" title="TLS Certificate Processing" aria-hidden="true"></i>`
    };
    return icons[status] || `<i class="fas fa-question-circle align-middle me-2 text-secondary" title="TLS Status Unknown" aria-hidden="true"></i>`;
  }

  errorCellHtml(message) {
    if (!message) { return '<span class="text-muted">—</span>'; }
    const truncated = message.length > 50 ? message.substring(0, 50) + '...' : message;
    return `<span class="text-danger" title="${message.replace(/"/g, '&quot;')}">${truncated}</span>`;
  }

  formatExpiryDate(iso) {
    if (!iso) { return '<span class="text-muted">—</span>'; }
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  buildDnsResponseHtml(response) {
    let html = `<p><strong>Message from Mailgun:</strong> ${response.message}</p>`;
    const data = response.data;
    if (!data) { return html; }

    if (data.domain) {
      html += `<p><strong>Domain:</strong> ${data.domain.name || this.domainName}</p>`;
      html += `<p><strong>State:</strong> ${data.domain.state || 'N/A'}</p>`;
    }
    if (data.sending_dns_records?.length)   { html += this.formatDnsRecords(data.sending_dns_records, 'Sending DNS Records'); }
    if (data.receiving_dns_records?.length) { html += this.formatDnsRecords(data.receiving_dns_records, 'Receiving DNS Records'); }
    return html;
  }

  formatDnsRecords(records, recordType) {
    let html = `<p><strong>${recordType}:</strong></p><ul>`;
    records.forEach((record) => {
      html += '<li>';
      html += `<strong>Type:</strong> ${record.record_type || 'N/A'}<br>`;
      html += `<strong>Name:</strong> ${record.name || 'N/A'}<br>`;
      if (record.value)    { html += `<strong>Value:</strong> ${record.value}<br>`; }
      if (record.priority) { html += `<strong>Priority:</strong> ${record.priority}<br>`; }
      html += `<strong>Valid:</strong> <span class="${record.valid === 'valid' ? 'text-success' : 'text-danger'}">${record.valid || 'N/A'}</span><br>`;
      html += `<strong>Active:</strong> ${record.is_active ? 'Yes' : 'No'}`;
      html += '</li>';
    });
    return html + '</ul>';
  }
}
